import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { ROLES_CRUD } from '../constants/roles';

function Header({ darkMode, setDarkMode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAuthenticated, isSuperAdmin } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileResearchOpen, setMobileResearchOpen] = useState(false);
  const [researchMenuOpen, setResearchMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const researchMenuRef = useRef(null);
  const isPublicMode = !isAuthenticated || !user;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
      if (researchMenuRef.current && !researchMenuRef.current.contains(event.target)) {
        setResearchMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const researchNavItems = [
    {
      path: '/dashboards',
      icon: 'analytics',
      label: 'Dashboards',
      description: 'Visualiza metricas y analisis globales',
    },
    {
      path: '/investigadores',
      icon: 'groups',
      label: 'Investigadores',
      description: 'Consulta perfiles, afiliaciones y produccion',
    },
    {
      path: '/publicaciones',
      icon: 'article',
      label: 'Publicaciones',
      description: 'Revisa el catalogo y los indicadores editoriales',
    },
    {
      path: '/lineas',
      icon: 'timeline',
      label: 'Lineas',
      description: 'Explora lineas y organizacion tematica',
    },
  ];

  const adminNavItems = [
    { path: '/busqueda-publicaciones', icon: 'manage_search', label: 'Publicaciones' },
    { path: '/formulario-ies', icon: 'assignment', label: 'Formulario IES' },
    { path: '/import', icon: 'cloud_upload', label: 'Importar' },
  ];

  const superAdminNavItems = [
    { path: '/users', icon: 'manage_accounts', label: 'Usuarios' },
    { path: '/publicaciones-jobs', icon: 'monitoring', label: 'Jobs publicaciones' },
  ];

  let visibleNavItems = [];

  if (isAuthenticated && user && ROLES_CRUD.includes(user.rol)) {
    visibleNavItems = [...visibleNavItems, ...adminNavItems];
  }

  if (isAuthenticated && isSuperAdmin && isSuperAdmin()) {
    visibleNavItems = [...visibleNavItems, ...superAdminNavItems];
  }

  const researchMenuActive = researchNavItems.some((item) => location.pathname === item.path);

  const handleLogout = () => {
    logout();
    setUserMenuOpen(false);
    navigate('/');
  };

  const handleMobileNavigation = () => {
    setMobileMenuOpen(false);
    setMobileResearchOpen(false);
    setResearchMenuOpen(false);
  };

  const getRoleColor = (rol) => {
    switch (rol) {
      case 'SuperAdmin':
        return 'from-purple-500 to-pink-500';
      case 'Administrador':
      case 'ADMIN_IES':
        return 'from-blue-500 to-cyan-500';
      case 'ADMIN_CEDIA':
        return 'from-violet-500 to-indigo-500';
      default:
        return 'from-green-500 to-teal-500';
    }
  };

  const getRoleBadgeColor = (rol) => {
    switch (rol) {
      case 'SuperAdmin':
        return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300';
      case 'Administrador':
      case 'ADMIN_IES':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
      case 'ADMIN_CEDIA':
        return 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300';
      default:
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
    }
  };

  const renderDesktopResearchLinks = () =>
    researchNavItems.map((item) => {
      const isActive = location.pathname === item.path;
      return (
        <NavLink
          key={item.path}
          to={item.path}
          className={`flex items-center gap-2 rounded-lg px-5 py-3 text-sm font-semibold transition-all ${
            isActive
              ? 'bg-[#2D5BA6] text-white shadow-md'
              : 'text-slate-700 hover:bg-slate-100 hover:text-[#2D5BA6] dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-[#5B8BD9]'
          }`}
        >
          <span className="material-icons-round text-xl">{item.icon}</span>
          <span className="tracking-wide">{item.label}</span>
        </NavLink>
      );
    });

  const renderMobileResearchLinks = () =>
    researchNavItems.map((item) => {
      const isActive = location.pathname === item.path;
      return (
        <NavLink
          key={item.path}
          to={item.path}
          onClick={handleMobileNavigation}
          className={`flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-semibold transition-all ${
            isActive
              ? 'bg-[#2D5BA6] text-white'
              : 'text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800'
          }`}
        >
          <span className="material-icons-round text-xl">{item.icon}</span>
          <span>{item.label}</span>
        </NavLink>
      );
    });

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between">
          <div className="flex items-center">
            <NavLink to="/" className="group flex items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-xl border-2 border-[#2D5BA6] bg-white shadow-md transition-all group-hover:shadow-lg dark:border-[#5B8BD9] dark:bg-slate-800">
                <img
                  src="/images/cedia.webp"
                  alt="CEDIA"
                  className="h-11 w-11 object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '<span class="material-icons-round text-2xl text-[#2D5BA6]">school</span>';
                  }}
                />
              </div>
            </NavLink>
          </div>

          <nav className="hidden lg:flex items-center space-x-2">
            {isPublicMode ? (
              renderDesktopResearchLinks()
            ) : (
              <div className="relative" ref={researchMenuRef}>
                <button
                  type="button"
                  onClick={() => setResearchMenuOpen((prev) => !prev)}
                  className={`flex items-center gap-2 rounded-lg px-5 py-3 text-sm font-semibold transition-all ${
                    researchMenuActive || researchMenuOpen
                      ? 'bg-[#2D5BA6] text-white shadow-md'
                      : 'text-slate-700 hover:bg-slate-100 hover:text-[#2D5BA6] dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-[#5B8BD9]'
                  }`}
                >
                  <span className="material-icons-round text-xl">science</span>
                  <span className="tracking-wide">Investigacion Cientifica</span>
                  <span className="material-icons-round text-lg">
                    {researchMenuOpen ? 'expand_less' : 'expand_more'}
                  </span>
                </button>

                <AnimatePresence>
                  {researchMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.96 }}
                      transition={{ duration: 0.15 }}
                      className="absolute left-0 mt-3 w-80 rounded-2xl border border-slate-200 bg-white p-3 shadow-xl dark:border-slate-700 dark:bg-slate-800"
                    >
                      <div className="mb-2 px-3 py-2">
                        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                          Explorar modulos
                        </p>
                        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                          Accede rapido a los espacios clave del seguimiento cientifico.
                        </p>
                      </div>
                      <div className="grid grid-cols-1 gap-2">
                        {researchNavItems.map((item) => {
                          const isActive = location.pathname === item.path;
                          return (
                            <NavLink
                              key={item.path}
                              to={item.path}
                              onClick={() => setResearchMenuOpen(false)}
                              className={`flex items-start gap-3 rounded-xl px-3 py-3 transition-all ${
                                isActive
                                  ? 'bg-[#2D5BA6] text-white shadow-md'
                                  : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700/70'
                              }`}
                            >
                              <span className="material-icons-round mt-0.5 text-xl">{item.icon}</span>
                              <div>
                                <div className="text-sm font-semibold">{item.label}</div>
                                <div className={`text-xs ${isActive ? 'text-blue-100' : 'text-slate-500 dark:text-slate-400'}`}>
                                  {item.description}
                                </div>
                              </div>
                            </NavLink>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {visibleNavItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-2 rounded-lg px-5 py-3 text-sm font-semibold transition-all ${
                    isActive
                      ? 'bg-[#2D5BA6] text-white shadow-md'
                      : 'text-slate-700 hover:bg-slate-100 hover:text-[#2D5BA6] dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-[#5B8BD9]'
                  }`}
                >
                  <span className="material-icons-round text-xl">{item.icon}</span>
                  <span className="tracking-wide">{item.label}</span>
                </NavLink>
              );
            })}
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setDarkMode(!darkMode)}
              className="rounded-lg p-3 text-slate-500 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800"
              title={darkMode ? 'Modo claro' : 'Modo oscuro'}
            >
              {darkMode ? (
                <span className="material-icons-round text-xl text-yellow-400">light_mode</span>
              ) : (
                <span className="material-icons-round text-xl">dark_mode</span>
              )}
            </button>

            {isAuthenticated && user ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-3 rounded-lg p-2 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  <div className={`flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br ${getRoleColor(user.rol)} text-base font-bold text-white shadow-md`}>
                    {user.username.charAt(0).toUpperCase()}
                  </div>
                  <span className="hidden text-sm font-semibold text-slate-700 dark:text-slate-300 md:block">
                    {user.username}
                  </span>
                  <span className="material-icons-round text-xl text-slate-400">
                    {userMenuOpen ? 'expand_less' : 'expand_more'}
                  </span>
                </button>

                <AnimatePresence>
                  {userMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 z-50 mt-2 w-64 rounded-xl border border-slate-200 bg-white py-2 shadow-xl dark:border-slate-700 dark:bg-slate-800"
                    >
                      <div className="border-b border-slate-100 px-4 py-3 dark:border-slate-700">
                        <p className="text-sm font-semibold text-slate-900 dark:text-white">{user.username}</p>
                        <p className="truncate text-xs text-slate-500 dark:text-slate-400">{user.email}</p>
                        <span className={`mt-2 inline-block rounded-full px-2 py-0.5 text-xs font-medium ${getRoleBadgeColor(user.rol)}`}>
                          {user.rol}
                        </span>
                      </div>

                      <div className="py-1">
                        <NavLink
                          to="/profile"
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700/50"
                        >
                          <span className="material-icons-round text-lg">person</span>
                          Mi Perfil
                        </NavLink>
                      </div>

                      <div className="border-t border-slate-100 pt-1 dark:border-slate-700">
                        <button
                          onClick={handleLogout}
                          className="flex w-full items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                        >
                          <span className="material-icons-round text-lg">logout</span>
                          Cerrar Sesion
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <NavLink
                to="/login"
                className="flex items-center gap-2 rounded-lg bg-[#2D5BA6] px-6 py-3 text-sm font-semibold text-white shadow-md transition-all hover:bg-[#244a8a] hover:shadow-lg"
              >
                <span className="material-icons-round text-xl">login</span>
                <span className="hidden sm:inline">Iniciar Sesion</span>
              </NavLink>
            )}

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="rounded-lg p-3 text-slate-500 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800 lg:hidden"
            >
              <span className="material-icons-round text-xl">
                {mobileMenuOpen ? 'close' : 'menu'}
              </span>
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="border-t border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900 lg:hidden"
          >
            <nav className="space-y-2 px-4 py-3">
              {isPublicMode ? (
                renderMobileResearchLinks()
              ) : (
                <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700">
                  <button
                    type="button"
                    onClick={() => setMobileResearchOpen((prev) => !prev)}
                    className={`flex w-full items-center justify-between px-4 py-3 text-sm font-semibold transition-all ${
                      researchMenuActive || mobileResearchOpen
                        ? 'bg-[#2D5BA6] text-white'
                        : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
                    }`}
                  >
                    <span className="flex items-center gap-3">
                      <span className="material-icons-round text-xl">science</span>
                      <span>Investigacion Cientifica</span>
                    </span>
                    <span className="material-icons-round text-lg">
                      {mobileResearchOpen ? 'expand_less' : 'expand_more'}
                    </span>
                  </button>

                  <AnimatePresence initial={false}>
                    {mobileResearchOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="space-y-1 bg-slate-50 p-2 dark:bg-slate-800/60"
                      >
                        {researchNavItems.map((item) => {
                          const isActive = location.pathname === item.path;
                          return (
                            <NavLink
                              key={item.path}
                              to={item.path}
                              onClick={handleMobileNavigation}
                              className={`flex items-start gap-3 rounded-lg px-3 py-3 text-sm transition-all ${
                                isActive
                                  ? 'bg-[#2D5BA6] text-white'
                                  : 'text-slate-700 hover:bg-white dark:text-slate-300 dark:hover:bg-slate-700/70'
                              }`}
                            >
                              <span className="material-icons-round mt-0.5 text-xl">{item.icon}</span>
                              <div>
                                <div className="font-semibold">{item.label}</div>
                                <div className={`text-xs ${isActive ? 'text-blue-100' : 'text-slate-500 dark:text-slate-400'}`}>
                                  {item.description}
                                </div>
                              </div>
                            </NavLink>
                          );
                        })}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {visibleNavItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={handleMobileNavigation}
                    className={`flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-semibold transition-all ${
                      isActive
                        ? 'bg-[#2D5BA6] text-white'
                        : 'text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800'
                    }`}
                  >
                    <span className="material-icons-round text-xl">{item.icon}</span>
                    <span>{item.label}</span>
                  </NavLink>
                );
              })}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

export default Header;
